/**
 * 팝업 배너의 "오늘 안보기" 상태를 localStorage에 효율적으로 관리하는 유틸리티
 * 단일 키에 JSON 객체로 모든 공지의 타임스탬프를 저장하여 localStorage 사용 최적화
 */

const STORAGE_KEY = "popup-banner-dont-show";
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000; // 24시간 (밀리초)

/**
 * localStorage에서 모든 공지의 타임스탬프 객체를 가져옴
 * @returns {Object} { bannerId: timestamp, ... } 형태의 객체
 */
const getDontShowData = () => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (!data) return {};

        const parsed = JSON.parse(data);
        return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch (error) {
        return {};
    }
};

/**
 * localStorage에 공지 타임스탬프 객체 저장
 * @param {Object} data - { bannerId: timestamp, ... } 형태의 객체
 */
const setDontShowData = (data) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
        // localStorage 접근 불가 시 무시
    }
};

/**
 * 오래된 항목(24시간 이상 지난 항목)을 정리하고 유효한 항목만 반환
 * @param {Object} data - 타임스탬프 객체
 * @returns {Object} 정리된 타임스탬프 객체
 */
const cleanExpiredItems = (data) => {
    const currentTime = Date.now();
    const cleaned = {};

    Object.keys(data).forEach((bannerId) => {
        const timestamp = parseInt(data[bannerId], 10);
        if (isNaN(timestamp)) return;

        const timeDiff = currentTime - timestamp;
        // 24시간이 지나지 않았으면 유지
        if (timeDiff < TWENTY_FOUR_HOURS) {
            cleaned[bannerId] = timestamp;
        }
    });

    return cleaned;
};

/**
 * 특정 공지가 24시간 내에 안보기로 설정되었는지 확인
 * @param {number|string} bannerId - 공지 ID
 * @returns {boolean} true면 표시 안함, false면 표시 가능
 */
export const isBannerHidden = (bannerId) => {
    if (!bannerId) return false;

    try {
        const data = getDontShowData();
        const timestamp = data[bannerId];

        if (!timestamp) return false; // 저장된 값이 없으면 표시 가능

        const currentTime = Date.now();
        const timeDiff = currentTime - parseInt(timestamp, 10);

        // 24시간이 지났으면 표시 가능
        return timeDiff < TWENTY_FOUR_HOURS;
    } catch (error) {
        return false; // 에러 시 표시 가능
    }
};

/**
 * 특정 공지를 24시간 동안 안보기로 설정
 * @param {number|string} bannerId - 공지 ID
 */
export const setBannerHidden = (bannerId) => {
    if (!bannerId) return;

    try {
        const data = getDontShowData();
        const cleanedData = cleanExpiredItems(data); // 오래된 항목 정리
        const currentTime = Date.now();

        cleanedData[bannerId] = currentTime;
        setDontShowData(cleanedData);
    } catch (error) {
        // localStorage 접근 불가 시 무시
    }
};

/**
 * localStorage에서 오래된 항목들을 정리 (주기적으로 호출 가능)
 */
export const cleanupExpiredBanners = () => {
    try {
        const data = getDontShowData();
        const cleanedData = cleanExpiredItems(data);

        // 정리된 데이터가 원본과 다르면 저장
        if (JSON.stringify(data) !== JSON.stringify(cleanedData)) {
            setDontShowData(cleanedData);
        }
    } catch (error) {
        // localStorage 접근 불가 시 무시
    }
};
